/**
 * Non-volatile status conditions — real modern-mainline (Gen 7+) mechanics.
 *
 * Pure + deterministic: every probabilistic gate (full-paralysis, freeze-thaw,
 * sleep duration) flows through an injected Rng (core/rng) so battles replay
 * identically. Nothing here renders, does IO, or mutates its inputs — helpers
 * return fresh instances + structured results the battle engine threads through.
 *
 * End-of-turn residual damage (fractions of maxHP, min 1):
 *   burn   → 1/16
 *   poison → 1/8
 *   toxic  → n/16, where n is a per-Pokemon counter that climbs 1,2,3,… each turn
 *            (badly-poisoned escalation)
 *
 * Action-blocking + speed:
 *   paralysis → effective Speed ×0.5; 25% "fully paralyzed, can't move"
 *   sleep     → can't move for a rolled 1–3 turns; wakes when the counter hits 0
 *   freeze    → can't move; 20% self-thaw each turn; ANY Fire move thaws it
 *
 * Immunities (applyStatus refuses, applied=false): Fire can't burn, Ice can't
 * freeze, Electric can't be paralyzed (Gen 6+), Poison/Steel can't be poisoned
 * (regular or toxic), and an already-statused mon can't take a second one.
 */
import type {
  PokemonInstance,
  StatusCondition,
  BattleParticipant,
  BattleState,
  BattleSideId,
} from '@/shared';
import type { Rng } from './rng.js';

/** Canonical residual-damage fractions of maxHP. */
export const BURN_FRACTION = 1 / 16;
export const POISON_FRACTION = 1 / 8;
/** Toxic damage is TOXIC_FRACTION * counter (1,2,3,…). */
export const TOXIC_FRACTION = 1 / 16;

/** Probability of "fully paralyzed" (can't move) on a paralyzed turn. */
const FULL_PARALYSIS_CHANCE = 0.25;
/** Probability a frozen mon thaws on its own at the start of its turn. */
const FREEZE_THAW_CHANCE = 0.2;
/** Sleep lasts a random duration in [SLEEP_MIN_TURNS, SLEEP_MAX_TURNS]. */
const SLEEP_MIN_TURNS = 1;
const SLEEP_MAX_TURNS = 3;

/** Result of an applyStatus attempt: a fresh instance + whether it took. */
export interface ApplyStatusResult {
  readonly applied: boolean;
  readonly instance: PokemonInstance;
}

/** What canMove decided this turn, plus the post-roll status to persist. */
export interface CanMoveResult {
  readonly canMove: boolean;
  /** Why it could/couldn't act: a blocking status, or a transition this turn. */
  readonly reason: 'none' | 'paralysis' | 'sleep' | 'freeze' | 'woke' | 'thawed';
  /** Status after this turn's roll (sleep/freeze may have cleared). */
  readonly updatedStatus: StatusCondition;
  /** Status counter after this turn (sleep decrements; others persist). */
  readonly updatedStatusTurns: number;
}

/** A single residual-damage event from the end-of-turn tick. */
export interface StatusDamageEvent {
  readonly side: BattleSideId;
  readonly instanceId: string;
  readonly status: StatusCondition;
  readonly amount: number;
  readonly remainingHp: number;
}

/** endOfTurnEffects output: events to animate + the advanced battle state. */
export interface EndOfTurnResult {
  readonly damageEvents: readonly StatusDamageEvent[];
  readonly updatedState: BattleState;
}

/** Result of a Fire-move thaw check: fresh instance + whether it thawed. */
export interface ThawResult {
  readonly thawed: boolean;
  readonly instance: PokemonInstance;
}

function lc(t: string): string {
  return t.toLowerCase();
}

/**
 * Would `status` be refused by `defenderTypes`'s elemental immunity? (Does NOT
 * consider an existing status — that's handled separately in applyStatus.)
 */
export function isImmuneToStatus(
  status: StatusCondition,
  defenderTypes: readonly string[],
): boolean {
  const types = defenderTypes.map(lc);
  switch (status) {
    case 'burn':
      return types.includes('fire');
    case 'freeze':
      return types.includes('ice');
    case 'paralysis':
      return types.includes('electric'); // Gen 6+
    case 'poison':
    case 'toxic':
      return types.includes('poison') || types.includes('steel');
    default:
      return false;
  }
}

/**
 * Try to inflict a non-volatile status. Respects type immunities and refuses to
 * overwrite an existing status. Returns a fresh instance (never mutates input).
 * `rng` is only consumed when rolling sleep's duration.
 */
export function applyStatus(
  instance: PokemonInstance,
  status: StatusCondition,
  defenderTypes: readonly string[],
  rng: Rng,
): ApplyStatusResult {
  if (status === 'none') return { applied: false, instance };
  if (instance.status !== 'none') return { applied: false, instance };
  if (isImmuneToStatus(status, defenderTypes)) return { applied: false, instance };

  let statusTurns = 0;
  if (status === 'sleep') {
    statusTurns = SLEEP_MIN_TURNS + rng.int(SLEEP_MAX_TURNS - SLEEP_MIN_TURNS + 1);
  } else if (status === 'toxic') {
    statusTurns = 1; // toxic counter starts at 1
  }

  return {
    applied: true,
    instance: { ...instance, status, statusTurns },
  };
}

/** Residual end-of-turn HP loss for a participant's status (≥1, or 0 if none). */
export function statusEndOfTurnDamage(participant: BattleParticipant): number {
  const { instance } = participant;
  const max = instance.maxHp;
  switch (instance.status) {
    case 'burn':
      return Math.max(1, Math.floor(max * BURN_FRACTION));
    case 'poison':
      return Math.max(1, Math.floor(max * POISON_FRACTION));
    case 'toxic': {
      const n = Math.max(1, instance.statusTurns);
      return Math.max(1, Math.floor(max * n * TOXIC_FRACTION));
    }
    default:
      return 0;
  }
}

/**
 * Effective Speed for turn-order: paralysis cuts it. We use the modern (Gen 7+)
 * value of ×0.5 (Gen 2–6 used ×0.25); the engine standardizes on modern mainline,
 * matching the Gen 6+ type chart and the ×1.5 crit.
 */
export function effectiveSpeed(speed: number, status: StatusCondition): number {
  return status === 'paralysis' ? Math.floor(speed * 0.5) : speed;
}

/**
 * Apply residual status damage to each side's ACTIVE mon and escalate toxic
 * counters. Returns the damage events + the advanced battle state. Pure: builds
 * fresh participant/side objects rather than mutating the input state.
 */
export function endOfTurnEffects(state: BattleState): EndOfTurnResult {
  const events: StatusDamageEvent[] = [];
  const sideIds = ['player', 'opponent'] as const;
  const nextSides: Record<BattleSideId, BattleState['sides'][BattleSideId]> = {
    ...state.sides,
  };

  for (const sideId of sideIds) {
    const side = state.sides[sideId];
    const active = side.party[side.activeIndex];
    if (!active) continue;
    const inst = active.instance;

    // Skip fainted mons and statuses that don't chip.
    const isResidual =
      inst.status === 'burn' || inst.status === 'poison' || inst.status === 'toxic';
    if (inst.currentHp <= 0 || !isResidual) continue;

    const raw = statusEndOfTurnDamage(active);
    const amount = Math.min(raw, inst.currentHp);
    const remainingHp = inst.currentHp - amount;

    const nextInstance: PokemonInstance = {
      ...inst,
      currentHp: remainingHp,
      // Toxic counter climbs each turn it survives.
      statusTurns: inst.status === 'toxic' ? inst.statusTurns + 1 : inst.statusTurns,
    };

    const nextParty = side.party.slice();
    nextParty[side.activeIndex] = { ...active, instance: nextInstance };
    nextSides[sideId] = { ...side, party: nextParty };

    events.push({
      side: sideId,
      instanceId: inst.id,
      status: inst.status,
      amount,
      remainingHp,
    });
  }

  return {
    damageEvents: events,
    updatedState: { ...state, sides: nextSides },
  };
}

/**
 * Can this mon act this turn? Rolls the probabilistic gates through `rng`:
 *  - sleep   → blocked; decrement the counter; wake (and may move) at 0
 *  - freeze  → 20% thaw (then may move); otherwise blocked
 *  - paralysis → 25% fully paralyzed (blocked); else may move (status persists)
 *  - burn/poison/toxic/none → always may move (no roll consumed)
 *
 * Non-mutating: returns the post-roll status for the caller to persist.
 */
export function canMove(instance: PokemonInstance, rng: Rng): CanMoveResult {
  switch (instance.status) {
    case 'sleep': {
      const remaining = instance.statusTurns - 1;
      if (remaining <= 0) {
        return { canMove: true, reason: 'woke', updatedStatus: 'none', updatedStatusTurns: 0 };
      }
      return {
        canMove: false,
        reason: 'sleep',
        updatedStatus: 'sleep',
        updatedStatusTurns: remaining,
      };
    }
    case 'freeze': {
      if (rng.int(5) === 0) {
        return { canMove: true, reason: 'thawed', updatedStatus: 'none', updatedStatusTurns: 0 };
      }
      return {
        canMove: false,
        reason: 'freeze',
        updatedStatus: 'freeze',
        updatedStatusTurns: instance.statusTurns,
      };
    }
    case 'paralysis': {
      const fullPara = rng.int(4) === 0;
      return {
        canMove: !fullPara,
        reason: fullPara ? 'paralysis' : 'none',
        updatedStatus: 'paralysis',
        updatedStatusTurns: instance.statusTurns,
      };
    }
    default:
      return {
        canMove: true,
        reason: 'none',
        updatedStatus: instance.status,
        updatedStatusTurns: instance.statusTurns,
      };
  }
}

/**
 * Any Fire-type move thaws a frozen target. Returns a fresh instance + whether a
 * thaw happened. No-op (thawed=false) if the move isn't Fire or the mon isn't frozen.
 */
export function thawFromFireMove(instance: PokemonInstance, moveType: string): ThawResult {
  if (instance.status === 'freeze' && lc(moveType) === 'fire') {
    return { thawed: true, instance: { ...instance, status: 'none', statusTurns: 0 } };
  }
  return { thawed: false, instance };
}

/**
 * Thin, rng-free predicate: does this status block acting given a pre-rolled
 * uniform `roll` in [0,1)? Sleep always blocks; paralysis blocks if roll is
 * inside the 25% full-para window; freeze blocks unless the roll thaws (20%);
 * burn/poison/toxic/none never block. (canMove is the rng-plumbed primary API;
 * this helper exists for callers that already hold a roll.)
 */
export function statusBlocksAction(status: StatusCondition, roll: number): boolean {
  switch (status) {
    case 'sleep':
      return true;
    case 'paralysis':
      return roll < FULL_PARALYSIS_CHANCE;
    case 'freeze':
      return roll >= FREEZE_THAW_CHANCE; // thaws (can act) only on the low slice
    default:
      return false;
  }
}
